import React from "react";
import "./index.sass";
import MarketsTableTabs from "./components/MarketsTableTabs";
import MarketsTableSearch from "./components/MarketsTableSearch";
import MarketsTableFilters from "./components/MarketsTableFilters";
import MarketsTableHeader from "./components/MarketsTableHeader";
import MarketsTableList from "./components/MarketsTableList";
import MarketsTablePagination from "./components/MarketsTablePagination";
import {
  useMarketsTableData,
  MARKETS_TABLE_CATEGORY,
} from "./hooks/useMarketsTableData";
import { useFuturesPairsSlugs } from "./hooks/useFuturesPairsSlugs";
import { useTranslation } from "@helpers/translate";

const MarketsTable = ({
  defaultCategory = MARKETS_TABLE_CATEGORY.all,
  hideTitle = false,
}) => {
  const { slugsSet: futuresPairsSlugsSet } = useFuturesPairsSlugs();
  const {
    category,
    search,
    isSearchOpen,
    searchInputRef,
    favorites,
    paginatedCoins,
    showPagination,
    paginationRange,
    currentPage,
    totalPages,
    sortBy,
    sortDirection,
    vsCurrency,
    segment,
    isLoading,
    handleCategoryChange,
    handleSearchChange,
    handleSearchToggle,
    handleSearchBlur,
    handleSearchClear,
    handleToggleFavorite,
    handlePageChange,
    handleEllipsisClick,
    handleSortChange,
    handleSegmentChange,
    handleVsCurrencyChange,
    isTradableOnly,
    handleTradableOnlyChange,
  } = useMarketsTableData(defaultCategory, futuresPairsSlugsSet);

  const { __ } = useTranslation();

  const TABLE_COLUMNS = [
    { key: "pair", label: __("markets.table.pair"), sortable: true },
    { key: "price", label: __("markets.table.price"), sortable: true },
    { key: "change", label: __("markets.table.change"), sortable: true },
    { key: "marketCap", label: __("markets.table.marketCap"), sortable: true, info: true },
    { key: "volume", label: __("markets.table.volume"), sortable: true, info: true },
    { key: "chart", label: __("markets.table.chart") },
    { key: "action", label: __("markets.table.action") },
  ];

  const CATEGORY_TABS = [
    { id: MARKETS_TABLE_CATEGORY.favorites, label: __("markets.tabs.favorites") },
    { id: MARKETS_TABLE_CATEGORY.all, label: __("markets.tabs.all") },
    {
      id: MARKETS_TABLE_CATEGORY.newListings,
      label: __("markets.tabs.newListings"),
    },
  ];
  const emptyStateMessage =
    category === MARKETS_TABLE_CATEGORY.favorites
      ? __("markets.noFavorites")
      : __("markets.noCoins");

  return (
    <div className="markets-table">
      {!hideTitle && (
        <h2 className="markets-table__title">{__("common.markets")}</h2>
      )}
      <div className="markets-table__header-container">
        <MarketsTableTabs
          tabs={CATEGORY_TABS}
          activeTab={category}
          onSelect={handleCategoryChange}
        />
        <MarketsTableSearch
          search={search}
          isSearchOpen={isSearchOpen}
          onToggle={handleSearchToggle}
          onChange={handleSearchChange}
          onBlur={handleSearchBlur}
          onClear={handleSearchClear}
          inputRef={searchInputRef}
        />
      </div>

      <MarketsTableFilters
        segment={segment}
        vsCurrency={vsCurrency}
        onSegmentChange={handleSegmentChange}
        onVsCurrencyChange={handleVsCurrencyChange}
        isTradableOnly={isTradableOnly}
        onTradableOnlyChange={handleTradableOnlyChange}
      />

      <div className="markets-table__table-wrapper">
        <table className="markets-table__table">
          <colgroup>
            <col className="markets-table__col markets-table__col--pair" />
            <col className="markets-table__col markets-table__col--price" />
            <col className="markets-table__col markets-table__col--change" />
            <col className="markets-table__col markets-table__col--market-cap" />
            <col className="markets-table__col markets-table__col--volume" />
            <col className="markets-table__col markets-table__col--chart" />
            <col className="markets-table__col markets-table__col--action" />
          </colgroup>

          <MarketsTableHeader
            columns={TABLE_COLUMNS}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={handleSortChange}
          />

          <MarketsTableList
            coins={paginatedCoins}
            favorites={favorites}
            onToggleFavorite={handleToggleFavorite}
            emptyState={emptyStateMessage}
            isLoading={isLoading}
            vsCurrency={vsCurrency}
            futuresPairsSlugsSet={futuresPairsSlugsSet}
          />
        </table>
      </div>

      {showPagination && (
        <div className="markets-table__pagination-container">
          <MarketsTablePagination
            paginationRange={paginationRange}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onEllipsisClick={handleEllipsisClick}
          />
        </div>
      )}
    </div>
  );
};

export default MarketsTable;
